import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import { useSearchParams } from "react-router-dom";
import { LEAD_STATUS_LABELS, type Destination, type User } from "@ckgo2/shared/types";
import { formatPrice, formatDate } from "@ckgo2/shared/utils";
import { LEAD_STATUS_CONFIG } from "@/lib/statusConfig";
import FacetedListLayout from "@/components/common/FacetedListLayout";
import FacetPanel, { type FacetBucket } from "@/components/common/FacetPanel";
import { Plus, LayoutGrid, UserCircle } from "lucide-react";

interface LeadRow {
  id: string;
  leadNumber: string;
  status: keyof typeof LEAD_STATUS_LABELS;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  destinationId?: string | null;
  assignedTo?: string | null;
  paxAdults: number;
  paxChildren: number;
  estimatedValue?: number | null;
  currency: string;
  leadScore: number;
  createdAt: string;
  assignedToName?: string | null;
  destinationName?: string | null;
  tripTitle?: string | null;
}

interface FacetedLeads {
  data: LeadRow[];
  total: number;
  page: number;
  limit: number;
  facets: Record<string, FacetBucket[]>;
  success: true;
}

export default function LeadsPage() {
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["leads", qs],
    queryFn: () => api.get<FacetedLeads>(`/api/admin/leads?${qs}`),
  });

  // Lookup lists for facet/row labels (ids → names).
  const { data: usersData } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get<ApiResponse<User[]>>("/api/admin/users"),
  });
  const { data: destData } = useQuery({
    queryKey: ["destinations"],
    queryFn: () => api.get<ApiResponse<Destination[]>>("/api/admin/destinations"),
  });

  const userName = (id: string) => usersData?.data.find((u) => u.id === id)?.name ?? id;
  const destName = (id: string) => destData?.data.find((d) => d.id === id)?.name ?? id;

  const leads = data?.data ?? [];
  const facets = data?.facets ?? {};
  const total = data?.total ?? 0;
  const limit = data?.limit ?? 25;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Inquiries</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Sales funnel — leads before they become orders
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/leads/new" className="btn-primary">
            <Plus className="h-5 w-5" />
            New lead
          </Link>
          <Link to="/leads/kanban" className="btn-ghost">
            <LayoutGrid className="h-5 w-5" />
            Kanban
          </Link>
        </div>
      </div>

      <FacetedListLayout
        searchPlaceholder="Search by name, email, phone, or number…"
        total={total}
        limit={limit}
        isLoading={isLoading}
        isFetching={isFetching}
        filters={
          <>
            <FacetPanel
              title="Status"
              paramKey="status"
              buckets={facets.status ?? []}
              labelFor={(v) => LEAD_STATUS_LABELS[v as keyof typeof LEAD_STATUS_LABELS] ?? v}
            />
            <FacetPanel
              title="Agent"
              paramKey="assignedTo"
              buckets={facets.assignedTo ?? []}
              labelFor={userName}
              nullLabel="Unassigned"
            />
            <FacetPanel
              title="Destination"
              paramKey="destinationId"
              buckets={facets.destinationId ?? []}
              labelFor={destName}
              nullLabel="—"
            />
          </>
        }
      >
        <div className="card overflow-hidden p-0">
          {leads.length === 0 ? (
            <div className="flex h-48 flex-col items-center justify-center text-gray-500">
              <UserCircle className="mb-2 h-10 w-10 text-gray-300" />
              <p>No leads found</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
                <tr>
                  {["Lead", "Contact", "Interest", "Pax", "Value", "Status", "Agent"].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {leads.map((lead) => {
                  const cfg = LEAD_STATUS_CONFIG[lead.status] ?? LEAD_STATUS_CONFIG.received;
                  return (
                    <tr key={lead.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-3">
                        <Link
                          to={`/leads/${lead.id}`}
                          className="text-primary-600 dark:text-primary-400 font-mono text-sm font-medium hover:underline"
                        >
                          {lead.leadNumber}
                        </Link>
                        <p className="text-xs text-gray-400">{formatDate(lead.createdAt)}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900 dark:text-white">
                          {lead.contactName || "—"}
                        </p>
                        <p className="text-xs text-gray-500">
                          {lead.contactEmail || lead.contactPhone || ""}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {lead.tripTitle || lead.destinationName || "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                        {lead.paxAdults + lead.paxChildren}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                        {lead.estimatedValue ? formatPrice(lead.estimatedValue) : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`badge ${cfg.className}`}>{cfg.label}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                        {lead.assignedToName || <span className="text-gray-400">Unassigned</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </FacetedListLayout>
    </div>
  );
}
